import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch } from '../store/hooks';
import { addToast } from '../store/slices/toastsSlice';
import { inviteApiService } from '../services/inviteApiService';
import { checkSession } from '../store/slices/authSlice';

interface RegisterFormData {
  email: string;
  name: string;
  password: string;
  confirmPassword: string;
}

export const Register: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<RegisterFormData>({
    email: '',
    name: '',
    password: '',
    confirmPassword: '',
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  // Validate invite token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        dispatch(addToast('Invalid invitation link', 'error'));
        navigate('/login');
        return;
      }

      try {
        const invite = await inviteApiService.validateInvite(token);
        setFormData(prev => ({ ...prev, email: invite.email }));
        setTokenValid(true);
      } catch (error: any) {
        dispatch(addToast(error.message || 'Invalid or expired invitation', 'error'));
        navigate('/login');
      } finally {
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [token, dispatch, navigate]);

  const handleFieldChange = (field: keyof RegisterFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear field error when user corrects it
    if (attemptedSubmit && validationErrors[field]) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const validateForm = (): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.password.trim()) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (!formData.confirmPassword.trim()) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setAttemptedSubmit(true);
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    if (!token) {
      dispatch(addToast('Invalid invitation token', 'error'));
      return;
    }

    setLoading(true);

    try {
      await inviteApiService.register({
        token,
        password: formData.password,
        name: formData.name,
      });

      // Refresh auth state
      await dispatch(checkSession()).unwrap();

      dispatch(addToast('Account created successfully!', 'success'));
      navigate('/');
    } catch (error: any) {
      dispatch(addToast(error.message || 'Registration failed', 'error'));
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={styles.loadingText}>Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return null; // Will be redirected
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Create Your Account</h1>
        <p style={styles.subtitle}>Welcome to Deb's Meatbuns Manager</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={formData.email}
              style={styles.inputReadOnly}
              disabled
              readOnly
            />
            <div style={styles.helpText}>
              This email address is pre-filled from your invitation
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => handleFieldChange('name', e.target.value)}
              style={{
                ...styles.input,
                ...(attemptedSubmit && validationErrors.name ? styles.inputError : {})
              }}
              placeholder="Your full name"
              disabled={loading}
            />
            {attemptedSubmit && validationErrors.name && (
              <div style={styles.error}>{validationErrors.name}</div>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={formData.password}
              onChange={e => handleFieldChange('password', e.target.value)}
              style={{
                ...styles.input,
                ...(attemptedSubmit && validationErrors.password ? styles.inputError : {})
              }}
              placeholder="Minimum 8 characters"
              disabled={loading}
            />
            {attemptedSubmit && validationErrors.password && (
              <div style={styles.error}>{validationErrors.password}</div>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Confirm Password</label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={e => handleFieldChange('confirmPassword', e.target.value)}
              style={{
                ...styles.input,
                ...(attemptedSubmit && validationErrors.confirmPassword ? styles.inputError : {})
              }}
              placeholder="Re-enter your password"
              disabled={loading}
            />
            {attemptedSubmit && validationErrors.confirmPassword && (
              <div style={styles.error}>{validationErrors.confirmPassword}</div>
            )}
          </div>

          <button
            type="submit"
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {})
            }}
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    padding: '20px'
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '40px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    width: '100%',
    maxWidth: '400px'
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '8px',
    textAlign: 'center'
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '32px',
    textAlign: 'center'
  },
  loadingText: {
    textAlign: 'center',
    color: '#666',
    fontSize: '16px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column'
  },
  formGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#555'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease'
  },
  inputReadOnly: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box',
    backgroundColor: '#f8fafc',
    color: '#666',
    cursor: 'not-allowed'
  },
  inputError: {
    borderColor: '#dc3545'
  },
  error: {
    color: '#dc3545',
    fontSize: '12px',
    marginTop: '6px'
  },
  helpText: {
    color: '#666',
    fontSize: '12px',
    marginTop: '6px'
  },
  button: {
    padding: '12px 20px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'background-color 0.2s ease'
  },
  buttonDisabled: {
    backgroundColor: '#90caf9',
    cursor: 'not-allowed'
  }
};
